import { useEffect, useState } from 'react'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import Spinner from '../ui/Spinner'
import EditableField from '../ui/EditableField'
import EditableTagField from '../ui/EditableTagField'
import { emailIsValid } from '../util/validation'
import { useMutation, useQuery } from '@apollo/client/react'
import CurrentUser from '../graphql/queries/CurrentUser'
import AddAdmin from '../graphql/queries/AddAdmin'
import RemoveAdmin from '../graphql/queries/RemoveAdmin'
import RemoveMember from '../graphql/queries/RemoveMember'
import ResendInvite from '../graphql/queries/ResendInvite'
import CancelInvite from '../graphql/queries/CancelInvite'
import UpdateRoom from '../graphql/queries/UpdateRoom'
import InviteByEmail from '../graphql/queries/InviteByEmail'
import SetAssistant from '../graphql/queries/SetAssistant'
import Room from '../graphql/queries/Room'
import { Box, Stack, Group, Text, TextInput, Button, Paper, Title, Pill, Switch, Select } from '@mantine/core'
import Joyride, { STATUS } from 'react-joyride'
import { useModalStack } from '../ui/ModalStack'
import { DJ_LIST } from './DJChooser'

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone').map(tz => ({ value: tz, label: tz }))

export default function RoomDetails({ roomId }) {

    const dispatch = useAppDispatch()
    const { pushModal } = useModalStack()
    const { data: userData } = useQuery(CurrentUser)
    const { loading: roomDataLoading, data: roomData } = useQuery(Room, { variables: { roomId } })
    const user = userData?.user
    const room = roomData?.room
    const admins = room?.admins
    const members = room?.members
    const owner = admins?.find( admin => admin.id === room?.owner.id )
    const userIsOwner = owner?.id === user?.id
    const userIsAdmin = admins?.find( admin => admin.id === user.id )
    const userIsGlobalAdmin = user.roles?.includes('admin')

    // Find the current assistant by looking for a member with "llm-assistant" role
    const currentAssistant = members?.find(member => member.roles?.includes('llm-assistant'))
    // Find the corresponding DJ from DJ_LIST if there's a current assistant
    const currentDJ = currentAssistant ? DJ_LIST.find(dj => dj.username === currentAssistant.username.toLowerCase()) : null

    const [addAdminMutation, { loading: addAdminLoading }] = useMutation(AddAdmin)
    const [removeAdminMutation] = useMutation(RemoveAdmin)
    const [removeMemberMutation] = useMutation(RemoveMember)
    const [resendInviteMutation, { loading: resendInviteLoading }] = useMutation(ResendInvite)
    const [cancelInviteMutation, { loading: cancelInviteLoading }] = useMutation(CancelInvite)
    const [updateRoomMutation] = useMutation(UpdateRoom)
    const [inviteByEmailMutation, { loading: inviteByEmailLoading }] = useMutation(InviteByEmail)
    const [setAssistantMutation] = useMutation(SetAssistant)

    const [name, setName] = useState('')
    const [topic, setTopic] = useState('')
    const [spinning, setSpinning] = useState('')
    const [isPublic, setIsPublic] = useState(false)
    const [timezone, setTimezone] = useState('')
    const [djAccess, setDjAccess] = useState('everyone')
    const [editing, setEditing] = useState(null)
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteMessage, setInviteMessage] = useState('')
    const [inviteError, setInviteError] = useState('')
    const [addAdminUsername, setAddAdminUsername] = useState('')

    // Onboarding tour state
    const [runTour, setRunTour] = useState(false)
    const [hasSeenRoomDetailsOnboarding, setHasSeenRoomDetailsOnboarding] = useState(true)
    const [hasSeenOwnerOnboarding, setHasSeenOwnerOnboarding] = useState(true)

    useEffect(() => {
        setName(room?.name || '')
        setTopic(room?.topic || '')
        setSpinning(room?.spinning || '')
        setIsPublic(room?.public || false)
        setTimezone(room?.timezone || '')
        setDjAccess(room?.djAccess || 'everyone')
    }, [room])

    // Check if curator needs room details onboarding
    useEffect(() => {
        if (user?.id && userIsAdmin && !roomDataLoading) {
            const seenRoomDetails = !!localStorage.getItem(`onboarding-room-details-seen-${user.id}`)
            const seenOwner = !!localStorage.getItem(`onboarding-owner-seen-${user.id}`)
            setHasSeenRoomDetailsOnboarding(seenRoomDetails)
            setHasSeenOwnerOnboarding(seenOwner)

            // Start tour when component loads for the first time
            if (!seenRoomDetails) {
                setTimeout(() => setRunTour(true), 500)
            } else if (!seenOwner && userIsOwner) {
                setTimeout(() => setRunTour(true), 500)
            }
        }
    }, [user?.id, userIsAdmin, userIsOwner, roomDataLoading])

    // Stop tour on unmount (navigating away)
    useEffect(() => {
        return () => {
            if (runTour) {
                setRunTour(false)
            }
        }
    }, [runTour])

    const curatorTourSteps = [
        {
            target: '[data-tour="room-name"]',
            content: "Click on the name of the room or the topic to change them.",
            disableBeacon: true,
        },
        {
            target: '[data-tour="room-spinning"]',
            content: 'Click here to change the tags the DJ will use when it picks tracks for the room. You can use genre tags, like "rock" or "jazz", moods like "happy", or an era like "80s".  You can add multiple tags separated by spaces to further refine the selection.  For example, "80s rock upbeat" will only select tracks that match all three tags.  You can even use operators like "or" and "not" or use parenthesis for groupings.  But the easiest way to get the kind of music you want is to tell the DJ what kind of music you want to hear.',
            disableBeacon: true,
        },
        {
            target: '[data-tour="invite-email"]',
            content: "Enter the email address of anyone you'd like to invite to this room here. You can remove room members by clicking the \"x\" next to their name in the members list.",
            disableBeacon: true,
        },
    ]

    const ownerTourSteps = [
        {
            target: '[data-tour="add-admin"]',
            content: "Enter the username of a room member here to add them to the curators list, but be careful! Granting curator powers allows the room member to change the room settings and add or remove tags from tracks. Make sure you fully trust someone before making them a curator. You can remove someone from your curators list by clicking the \"x\" next to their name in the curators list.",
            disableBeacon: true,
        },
        {
            target: '[data-tour="choose-dj"]',
            content: "Use this button to change the DJ for this room.  When your room has a DJ, they'll respond to room chat when you mention their name.  DJs can provide information about tracks, suggest tracks, take requests, and help you manage the room's settings.",
            disableBeacon: true,
        }
    ]

    const handleJoyrideCallback = (data) => {
        const { status } = data

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            // Mark onboarding as seen
            if (user?.id) {
                if (userIsOwner && hasSeenRoomDetailsOnboarding) {
                    localStorage.setItem(`onboarding-owner-seen-${user.id}`, 'true')
                    setHasSeenOwnerOnboarding(true)
                } else {
                    localStorage.setItem(`onboarding-room-details-seen-${user.id}`, 'true')
                    setHasSeenRoomDetailsOnboarding(true)
                }
            }
            setRunTour(false)
        }
    }

    async function addAdmin() {
        if (room?.admins.find( a => a.username === addAdminUsername )) {
            dispatch(Actions.showAlertModal('That user is already an admin.'))
            return
        }
        const member = room?.members.find( m => m.username === addAdminUsername )
        if (member) {
            await addAdminMutation({ variables: { roomId, adminId: member.id } })
            setAddAdminUsername('')
        } else {
            dispatch(Actions.showAlertModal('There is no room member with that username.'))
        }
    }

    function removeAdmin(person) {
        if (user.id !== owner.id) return
        if (person.id === owner.id) {
            dispatch(Actions.showAlertModal('You are the owner of this room.  Owners always have admin privileges.'))
        } else {
            dispatch(Actions.showConfirmModal(
                `Are you sure you want to remove admin privileges for ${person.username}?  You will lose access to all of the music they have contributed to this room.`,
                'Remove Admin and their music',
                async () => {
                    await removeAdminMutation({ variables: { roomId, adminId: person.id } })
                }
            ))
        }
    }

    function removeMember(person) {
        if (admins.find( admin => admin.id === person.id )) {
            dispatch(Actions.showAlertModal('This person is an admin.  In order to remove them the owner of the room will need to revoke their admin status.'))
        } else {
            dispatch(Actions.showConfirmModal(
                `Are you sure you want to remove ${person.username}?`,
                'Remove Member',
                async () => {
                    await removeMemberMutation({ variables: { roomId, memberId: person.id } })
                }
            ))
        }
    }

    async function resendEmailInvite(inviteId) {
        setInviteMessage('')
        setInviteError('')
        try {
            await resendInviteMutation({ variables: { inviteId }, refetchQueries: [{ query: Room, variables: { roomId } }] })
            setInviteMessage('Invitation re-sent!')
        } catch (error) {
            setInviteError(error.message)
        }
    }

    async function cancelEmailInvite(inviteId) {
        setInviteMessage('')
        setInviteError('')
        try {
            await cancelInviteMutation({ variables: { inviteId }, refetchQueries: [{ query: Room, variables: { roomId } }] })
            setInviteMessage('Invitation cancelled!')
        } catch (error) {
            setInviteError(error.message)
        }
    }

    async function saveField(field, value) {
        if (room?.[field] !== value) {
            await updateRoomMutation({ variables: { roomId, updates: { [field]: value  } } })
        }
    }

    async function sendEmailInvite() {
        setInviteMessage('')
        setInviteError('')
        try {
            await inviteByEmailMutation({ variables: { roomId, email: inviteEmail }, refetchQueries: [{ query: Room, variables: { roomId } }] })
            setInviteMessage('Invitation sent!')
            setInviteEmail('')
        } catch (error) {
            setInviteError(error.message)
        }
    }

    function updateSpinning(text) {
        const newSpinning = text.replaceAll(/[^a-zA-Z0-9_ \-()]/g, '')
        setSpinning(newSpinning)
    }

    async function togglePublic(value) {
        await updateRoomMutation({
            variables: { roomId, updates: { public: value } },
            optimisticResponse: {
                __typename: 'Mutation',
                updateRoom: {
                    __typename: 'UpdateRoomResult',
                    id: roomId,
                    room: {
                        ...room,
                        __typename: 'Room',
                        public: value,
                        updated: new Date().toISOString()
                    },
                    updated: new Date().toISOString()
                }
            }
        })
    }

    function handlePickDJ() {
        console.log('Opening DJ chooser with currentDJ:', currentDJ)
        console.log('Current assistant:', currentAssistant)
        pushModal('djChooser', {
            initialDJ: currentDJ,
            onSelectDJ: async (dj) => {
                // Set assistant with the selected DJ's username, or null for no DJ
                await setAssistantMutation({
                    variables: {
                        roomId,
                        username: dj?.username || null
                    },
                    refetchQueries: [{ query: Room, variables: { roomId } }]
                })
            }
        })
    }

    if (roomDataLoading) return (
        <Box p="xl" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner />
        </Box>
    )

    // Determine which tour to run
    const tourSteps = !hasSeenRoomDetailsOnboarding ? curatorTourSteps : ownerTourSteps
    const shouldRunTour = runTour && userIsAdmin && (!hasSeenRoomDetailsOnboarding || (!hasSeenOwnerOnboarding && userIsOwner))

    return (
        <Box p="md">
            <Joyride
                steps={tourSteps}
                run={shouldRunTour}
                continuous
                hideCloseButton
                showSkipButton
                disableOverlayClose
                disableCloseOnEsc
                locale={{ skip: 'Exit', last: 'Finish' }}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        primaryColor: '#ec03fc',
                        zIndex: 10001,
                    },
                }}
            />
            <Stack gap="md">
                {/* Room Basic Info Section */}
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Stack gap="sm">
                        <div data-tour="room-name">
                            <EditableField
                                label="Name"
                                value={name}
                                onChange={setName}
                                fieldName="name"
                                editing={editing}
                                setEditing={setEditing}
                                onSave={saveField}
                                canEdit={userIsOwner}
                            />
                        </div>
                        <EditableField
                            label="Topic"
                            value={topic}
                            onChange={setTopic}
                            fieldName="topic"
                            editing={editing}
                            setEditing={setEditing}
                            onSave={saveField}
                            canEdit={userIsAdmin}
                        />
                        <div data-tour="room-spinning">
                            <EditableTagField
                                label="Spinning"
                                value={spinning}
                                onChange={updateSpinning}
                                fieldName="spinning"
                                editing={editing}
                                setEditing={setEditing}
                                onSave={saveField}
                                roomId={roomId}
                                canEdit={userIsAdmin}
                            />
                        </div>
                        <Group gap="xs" align="flex-start" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                Owner
                            </Text>
                            <Text size="sm">{owner?.username}</Text>
                        </Group>
                        <Group gap="xs" align="center" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                Public
                            </Text>
                            <Switch
                                checked={isPublic}
                                onChange={(event) => togglePublic(event.currentTarget.checked)}
                                disabled={!userIsOwner}
                            />
                        </Group>
                        <Group gap="xs" align="center" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                DJ
                            </Text>
                            <Text size="sm">
                                {currentDJ ? currentDJ.name : (currentAssistant ? currentAssistant.username : 'No DJ selected')}
                            </Text>
                            {(userIsOwner || userIsGlobalAdmin) && (
                                <Button
                                    size="xs"
                                    variant="light"
                                    onClick={handlePickDJ}
                                    data-tour="choose-dj"
                                >
                                    {currentAssistant ? 'Change DJ' : 'Pick a DJ'}
                                </Button>
                            )}
                        </Group>
                        <Group gap="xs" align="center" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                DJ Access
                            </Text>
                            <Select
                                value={djAccess}
                                onChange={(value) => {
                                    if (!value) return
                                    setDjAccess(value)
                                    saveField('djAccess', value)
                                }}
                                data={[
                                    { value: 'everyone', label: 'Everyone' },
                                    { value: 'curators', label: 'Curators and owner' },
                                    { value: 'owner', label: 'Owner only' },
                                ]}
                                disabled={!userIsOwner}
                                size="sm"
                                style={{ flex: 1, maxWidth: 300 }}
                                withinPortal={false}
                                comboboxProps={{ zIndex: 2000 }}
                            />
                        </Group>
                        <Group gap="xs" align="center" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                Timezone
                            </Text>
                            <Select
                                value={timezone || null}
                                onChange={(value) => {
                                    setTimezone(value || '')
                                    saveField('timezone', value || '')
                                }}
                                data={TIMEZONE_OPTIONS}
                                placeholder="Select timezone"
                                searchable
                                clearable
                                disabled={!userIsAdmin}
                                size="sm"
                                style={{ flex: 1, maxWidth: 300 }}
                                withinPortal={false}
                                comboboxProps={{ zIndex: 2000 }}
                            />
                        </Group>
                    </Stack>
                </Paper>

                {/* Admins Section */}
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Stack gap="sm">
                        <Title order={5} c="gray.3">Curators</Title>
                        <Group gap="xs">
                            {admins?.map((admin) => (
                                <Pill
                                    key={admin.id}
                                    withRemoveButton={userIsOwner && admin.id !== owner.id}
                                    onRemove={() => removeAdmin(admin)}
                                    bg="cyan.9"
                                >
                                    {admin.username}
                                </Pill>
                            ))}
                        </Group>
                        {userIsOwner && (
                            <>
                                <Group gap="xs" align="flex-end">
                                    <TextInput
                                        value={addAdminUsername}
                                        onChange={(e) => setAddAdminUsername(e.target.value)}
                                        placeholder="Username"
                                        size="sm"
                                        style={{ flex: 1 }}
                                        data-tour="add-admin"
                                    />
                                    <Button
                                        onClick={addAdmin}
                                        disabled={addAdminLoading || !addAdminUsername}
                                        size="sm"
                                        loading={addAdminLoading}
                                    >
                                        Add Curator
                                    </Button>
                                </Group>
                            </>
                        )}
                    </Stack>
                </Paper>

                {/* Members Section */}
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Stack gap="sm">
                        <Title order={5} c="gray.3">Members</Title>
                        <Group gap="xs">
                            {members?.map((member) => (
                                <Pill
                                    key={member.id}
                                    withRemoveButton={userIsAdmin && !admins.find(admin => admin.id === member.id)}
                                    onRemove={() => removeMember(member)}
                                    bg="cyan.9"
                                >
                                    {member.username}
                                </Pill>
                            ))}
                        </Group>
                    </Stack>
                </Paper>

                {/* Invitations Section */}
                {userIsAdmin && (
                    <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <Stack gap="sm">
                            <Title order={5} c="gray.3">Invitations</Title>
                            {room?.invites && room.invites.length > 0 && (
                                <Stack gap="xs">
                                    {room.invites.map((invite) => (
                                        <Group key={invite.id} justify="space-between" align="center">
                                            <Text size="sm">{invite.email}</Text>
                                            <Group gap="xs">
                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    onClick={() => resendEmailInvite(invite.id)}
                                                    loading={resendInviteLoading}
                                                >
                                                    Resend
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    color="red"
                                                    onClick={() => cancelEmailInvite(invite.id)}
                                                    loading={cancelInviteLoading}
                                                >
                                                    Cancel
                                                </Button>
                                            </Group>
                                        </Group>
                                    ))}
                                </Stack>
                            )}
                            <Group gap="xs" align="flex-end">
                                <TextInput
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="Email address"
                                    size="sm"
                                    style={{ flex: 1 }}
                                    error={inviteError}
                                    data-tour="invite-email"
                                />
                                <Button
                                    onClick={sendEmailInvite}
                                    disabled={inviteByEmailLoading || !emailIsValid(inviteEmail)}
                                    size="sm"
                                    loading={inviteByEmailLoading}
                                >
                                    Send Invite
                                </Button>
                            </Group>
                            {inviteMessage && (
                                <Text size="sm" c="green">{inviteMessage}</Text>
                            )}
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Box>
    )
}
